import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Film, Loader2, SearchX, TriangleAlert } from "lucide-react";

import { CaptionClipDesk } from "@/components/video/CaptionClipDesk";
import { CaptionClipList } from "@/components/video/CaptionClipList";
import { CAPTION_SEARCH_MIN_LENGTH } from "@/hooks/use-video-caption-search";
import { slateCardClass } from "@/lib/card-styles";
import type { VideoCaptionHit } from "@/lib/video-types";
import { cn } from "@/lib/utils";

export interface ClipWatchViewProps {
  /** The word whose moments these are — highlights the match in every line. */
  query: string;
  hits: VideoCaptionHit[];
  isLoading: boolean;
  isError: boolean;
  /** The query is long enough to have searched. */
  isSearchable: boolean;
  activeClip: VideoCaptionHit | null;
  onSelectClip: (hit: VideoCaptionHit) => void;
  /**
   * The "open every clip" link. The word dialog keeps it; the pronunciation
   * palette makes its footer do that job instead, so it passes nothing.
   */
  allClipsHref?: string;
  /** Small label inside the rail's header, e.g. "Moments". */
  railHeader?: React.ReactNode;
}

/**
 * The watch half of "in context": a player, the clip rail beside it, and one
 * sentence for each state that is not a result.
 *
 * Extracted from `WordInContextDialog` so the pronunciation search and the
 * session sidebar's word dialog are the *same* surface, not two copies — the
 * whole point of clicking a spoken line is that you get the same play, slow
 * down and shadow behaviour wherever you found it.
 */
export function ClipWatchView({
  query,
  hits,
  isLoading,
  isError,
  isSearchable,
  activeClip,
  onSelectClip,
  allClipsHref,
  railHeader,
}: ClipWatchViewProps) {
  const { t } = useTranslation();

  const showEmpty = isSearchable && !isLoading && !isError && hits.length === 0;

  return (
    <>
      <div role="status" aria-live="polite" className="space-y-3">
        {!isSearchable && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t("pages.videos.inContext.prompt", {
              min: CAPTION_SEARCH_MIN_LENGTH,
              defaultValue:
                "Type at least {{min}} characters to search every caption line in the library.",
            })}
          </p>
        )}

        {isLoading && (
          <p className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {t("pages.videos.inContext.searching", "Searching captions…")}
          </p>
        )}

        {isError && (
          <p className="flex items-center justify-center gap-2 py-8 text-sm text-destructive">
            <TriangleAlert className="h-4 w-4" aria-hidden="true" />
            {t(
              "pages.videos.inContext.error",
              "Caption search is unavailable right now. Try again in a moment.",
            )}
          </p>
        )}

        {showEmpty && (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <SearchX className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-foreground">
              {t(
                "pages.videos.inContext.wordDialog.empty",
                "No clip in the library says this word yet.",
              )}
            </p>
            <p className="max-w-sm text-xs text-muted-foreground">
              {t(
                "pages.videos.inContext.noResultsHint",
                "Try one common word on its own — the search matches whole words, so a phrase has to appear exactly as spoken.",
              )}
            </p>
          </div>
        )}
      </div>

      {/* Clips beside the player from `sm` up: one column of "video then list"
          is a page, not a dialog, and it is what made the modal scroll on every
          word.
          The rail is a fixed 18rem, not a half: this is an index beside a
          player, and an even split gave a list of two-line titles the same
          width as the video. The same `minmax(0,1fr)` track keeps the player
          from being pushed out by the desk's own max-content width.
          `sm:grid-rows-[minmax(0,1fr)]` pins the row to the body's height. An
          `auto` row sizes to its tallest item, so a 500-character caption line
          made the row 779px inside a 558px body: the card ran past the footer
          *and* the rail lost its own scroll (its box grew with the row). */}
      <div className="grid min-h-0 gap-4 sm:flex-1 sm:grid-cols-[minmax(0,1fr)_18rem] sm:grid-rows-[minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-2 sm:min-h-0 sm:overflow-y-auto sm:pr-1">
          {activeClip && (
            /**
             * `w-full` and **no** `self-start`. The card sits in a column flex,
             * where `self-start` is the *horizontal* axis: it made the card
             * fit-content, so a narrow host (the pronunciation palette, 427px a
             * column) laid the desk out at its 513px max-content width and the
             * player painted 69px over the clip rail. A flex item is already
             * content-height here, so nothing needs `self-start` to keep the
             * caption line and "Open full lesson" inside the card.
             *
             * `shrink-0` is load-bearing: `overflow-hidden` makes a flex item's
             * automatic minimum size 0, so without it the card shrank to the
             * column's height and clipped a long caption instead of letting the
             * column scroll.
             */
            <div className={cn(slateCardClass, "w-full shrink-0 overflow-hidden")}>
              <CaptionClipDesk
                hits={hits}
                query={query}
                activeClip={activeClip}
                onSelect={onSelectClip}
              />
            </div>
          )}

          {allClipsHref && hits.length > 0 && (
            <Link
              to={allClipsHref}
              className="mt-auto inline-flex items-center gap-1.5 self-start text-xs font-medium text-primary hover:underline"
            >
              <Film className="h-3.5 w-3.5" aria-hidden="true" />
              {t(
                "pages.videos.inContext.wordDialog.allClips",
                "Open Pronunciation in context for every clip",
              )}
            </Link>
          )}
        </div>

        {hits.length > 0 && (
          <div className="min-h-0 min-w-0 overflow-y-auto sm:pr-1">
            <CaptionClipList
              hits={hits}
              query={query}
              activeVideoRowId={activeClip?.videoRowId ?? null}
              activeStart={activeClip?.start ?? null}
              onSelect={onSelectClip}
              header={railHeader}
            />
          </div>
        )}
      </div>
    </>
  );
}
